import type { ISO8601DateOnly, ISO8601Timestamp, ISO8601YearAndMonthOnly } from '../types/common.ts';

export function timestamp(date = new Date()): ISO8601Timestamp {
  return date.toISOString() as ISO8601Timestamp;
}

export function dateUtc(date = new Date()): ISO8601DateOnly {
  return date.toISOString().slice(0, 10) as ISO8601DateOnly;
}

export function dateLocal(date: Date | ISO8601Timestamp = new Date()): ISO8601DateOnly {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  return `${dateObj.getFullYear()}-${(dateObj.getMonth() + 1).toString().padStart(2, '0')}-${dateObj
    .getDate()
    .toString()
    .padStart(2, '0')}` as ISO8601DateOnly;
}

export function dateToLocaleDateString(date: ISO8601DateOnly): string {
  const [year, month, day] = date.split('-');
  return new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0).toLocaleDateString();
}

export function dateToWeekStart(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  while (date.getUTCDay() !== 0) {
    date.setUTCDate(date.getUTCDate() - 1);
  }

  return dateUtc(date);
}

export function dateToWeekEnd(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  while (date.getUTCDay() !== 6) {
    date.setUTCDate(date.getUTCDate() + 1);
  }

  return dateUtc(date);
}

export function dateToMonthStart(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  date.setUTCDate(1);
  return dateUtc(date);
}

export function dateToMonthEnd(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + 1);
  date.setUTCDate(0);
  return dateUtc(date);
}

export function dateToYearAndMonth(dateStr: ISO8601DateOnly): ISO8601YearAndMonthOnly {
  return dateStr.slice(0, 7) as ISO8601YearAndMonthOnly;
}

const monthToQuarter = {
  0: 1,
  1: 1,
  2: 1,
  3: 2,
  4: 2,
  5: 2,
  6: 3,
  7: 3,
  8: 3,
  9: 4,
  10: 4,
  11: 4,
} as const;

export function dateToQuarterStart(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  const quarter = monthToQuarter[date.getUTCMonth() as keyof typeof monthToQuarter];
  date.setUTCDate(1);
  date.setUTCMonth(quarter * 3 - 3);
  return dateUtc(date);
}

export function dateToQuarterEnd(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  const quarter = monthToQuarter[date.getUTCMonth() as keyof typeof monthToQuarter];
  date.setUTCDate(1);
  date.setUTCMonth(quarter * 3);
  date.setUTCDate(0);
  return dateUtc(date);
}

export function dateToHalfYearStart(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() < 6 ? 0 : 6);
  return dateUtc(date);
}

export function dateToHalfYearEnd(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() < 6 ? 6 : 12);
  date.setUTCDate(0);
  return dateUtc(date);
}

export function dateToYear(dateStr: ISO8601DateOnly): number {
  return Number(dateStr.slice(0, 4));
}

export function dateToYearStart(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  date.setUTCDate(1);
  date.setUTCMonth(0);
  return dateUtc(date);
}

export function dateToYearEnd(dateStr: ISO8601DateOnly): ISO8601DateOnly {
  const date = new Date(dateStr);
  date.setUTCDate(1);
  date.setUTCMonth(11);
  date.setUTCDate(31);
  return dateUtc(date);
}
